/**
 * Centralized trust-building content extracted from amerikooler.com
 * Used across: Shared Quote, PDF, Wizard tooltips, Warranty page
 * 
 * Rules:
 * - Max 3-5 bullets per section
 * - No paragraphs — scannable only
 * - Sales-oriented, not catalog-like
 */

export const CERTIFICATIONS = [
  { label: 'UL Listed Panels & Doors', detail: 'Standard 723, Edition 11 — flame spread & smoke tested' },
  { label: 'NSF International Certified', detail: 'Compliance with NSF Standard #7 — authorized to bear the NSF mark' },
  { label: 'Miami-Dade Hurricane Approved', detail: 'NOA #22-0914.05 — certified for 170 mph HVHZ winds' },
  { label: 'Florida Building Code', detail: '#FL22413R2 & #FL22413R3 — hurricane rated' },
  { label: 'DOE Energy Compliant', detail: 'Title 10 CFR 431.306 & ASTM C518' },
  { label: 'City of Los Angeles LADBS', detail: 'Research Report RR 26189, Approval CSI #13030' },
  { label: 'EISA Federal Requirements', detail: 'Meets & exceeds Energy Independence & Security Act — R-29 coolers, R-32 freezers' },
  { label: 'ASTM C272M-18', detail: 'Hydrophobic XPS insulation independently tested' },
  { label: 'City of Houston Certified', detail: 'Certificate No. 694 — exterior coolers & freezers' },
  { label: 'State of Oregon Licensed', detail: 'Approved manufacturer of structural insulated panels' },
] as const;

/** Short certification labels for badges / compact displays */
export const CERTIFICATION_LABELS = CERTIFICATIONS.map(c => c.label);

export const QUALITY_BULLETS = [
  'XPS insulation by DuPont — 50-year thermal warranty',
  '26 Ga. stucco embossed Acrylume panels',
  'Cam-lock assembly — no special tools needed',
  'Self-closing door with magnetic gasket & safety release',
  'CFC-free injected polyurethane foam core',
] as const;

export const COMPANY_TRUST = {
  tagline: 'Taking Cool Further™',
  facility: '200,000 sq. ft. advanced manufacturing facility',
  position: '#1 choice for keeping items Everyday Kool',
  description: 'Most reliable walk-in coolers & freezers for everyday businesses',
  phone: '(305) 715-1101',
  phoneTel: '+13057151101',
  email: 'sales@amerikooler.com',
  website: 'amerikooler.com',
} as const;

/** Short product descriptions for wizard micro-help */
export const WIZARD_TIPS = {
  cooler: 'Walk-in cooler for fresh food storage (35°F–40°F)',
  freezer: 'Walk-in freezer for frozen storage (-10°F)',
  indoor: 'Installed inside an existing structure',
  outdoor: 'Freestanding unit — includes membrane rainroof',
  selfContained: 'All-in-one unit mounted on the box — simplest install',
  remote: 'Condensing unit installed separately — quieter operation',
  xpsInsulation: 'DuPont XPS — superior R-value vs. EPS, 50-year warranty',
  camLock: 'Tool-free assembly in hours, not days',
  hurricaneRated: 'Certified for 170 mph winds (Miami-Dade NOA #22-0914.05 & FL Building Code)',
} as const;

/** Warranty summary for PDF/shared quote */
export const WARRANTY_SUMMARY = {
  panels: '15-year limited panel warranty',
  refrigeration: '5-year compressor & refrigeration warranty',
  doorHardware: '3-year door hardware warranty',
  floor: '10-year floor system warranty',
  insulation: '50-year thermal warranty (DuPont XPS)',
  hardware: '1-year warranty on hardware & accessories',
} as const;

/** For PDF terms — replaces generic text with real data */
export const TERMS_UPDATES = {
  warranty: 'Panels: 15-year limited warranty. Refrigeration: 5-year warranty. Door hardware: 3-year warranty. Floor: 10-year warranty. Insulation: 50-year thermal warranty (DuPont XPS).',
  compliance: 'All units manufactured in compliance with UL, NSF, DOE, EISA, and applicable state/local building codes. Hurricane-rated options certified by Miami-Dade County (NOA #22-0914.05) and Florida Building Code (#FL22413).',
} as const;

/** Installation quick-reference for Warranty & Support page */
export const INSTALLATION_STEPS = [
  {
    step: '1',
    title: 'Site Preparation',
    items: [
      'Ensure level concrete pad or reinforced floor rated for unit weight',
      'Verify minimum clearances: 12" from walls, 24" above for air circulation',
      'Confirm electrical supply matches equipment specifications',
      'Install vapor barrier if required for freezer units',
    ],
  },
  {
    step: '2',
    title: 'Panel Assembly',
    items: [
      'Start with floor panels (if applicable) — align cam-locks and engage',
      'Erect corner panels first, then fill wall sections',
      'Apply food-grade sealant at all panel joints',
      'Install ceiling panels last — use support braces during installation',
    ],
  },
  {
    step: '3',
    title: 'Door Installation',
    items: [
      'Mount door frame with provided hardware and shims for plumb',
      'Adjust self-closing hinges to proper tension',
      'Verify gasket seal around entire perimeter',
      'Test door heater operation (freezer doors)',
    ],
  },
  {
    step: '4',
    title: 'Refrigeration Setup',
    items: [
      'Mount evaporator per manufacturer specifications',
      'Connect refrigerant lines (self-contained units are pre-charged)',
      'Verify electrical connections and amp draw',
      'Allow unit to reach operating temperature before loading product',
    ],
  },
] as const;

/** Troubleshooting FAQ for Warranty & Support page */
export const TROUBLESHOOTING_ITEMS = [
  {
    issue: 'Unit not reaching temperature',
    solutions: [
      'Check thermostat settings and sensor placement',
      'Verify condenser coils are clean and unobstructed',
      'Inspect door gaskets for tears or gaps',
      'Confirm adequate airflow around condenser unit',
    ],
  },
  {
    issue: 'Excessive frost buildup on evaporator',
    solutions: [
      'Verify defrost timer is functioning correctly',
      'Check defrost heater continuity',
      'Inspect drain line for blockage',
      'Ensure door is sealing properly — check gasket and closer',
    ],
  },
  {
    issue: 'Water leaking inside unit',
    solutions: [
      'Clear condensate drain line',
      'Check drain pan heater (freezer units)',
      'Verify panel joints are properly sealed',
      'Inspect ceiling penetrations for roof-mounted condensers',
    ],
  },
  {
    issue: 'Compressor running continuously',
    solutions: [
      'Check refrigerant charge level',
      'Verify thermostat calibration',
      'Inspect for air leaks around doors, panels, or penetrations',
      'Clean condenser and evaporator coils',
    ],
  },
  {
    issue: 'Door not closing properly',
    solutions: [
      'Adjust self-closing hinge tension',
      'Check for obstructions in door sweep area',
      'Verify door frame is plumb and square',
      'Replace worn gaskets if seal is compromised',
    ],
  },
] as const;
